// 編成の「術」と「妖力」の札：押すと中身（今の装備・スキル込みの数値）を小窓で出す
import { BATTLE_CONFIG as CFG, CHARS, JUTSU, JUTSU_BY_ID, LINE_OUGI_LV, skillLines, type BattleLoadout, type CharId } from '@hyakki/core';
import { esc } from './dom';
import { icon } from './icons';
import { openModal } from './overlay';

const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;
const times = (v: number) => `${Math.round(v * 100) / 100}倍`;
const row = (k: string, v: string, note = '') => `<tr><th>${esc(k)}</th><td>${esc(v)}${note ? `<small>${esc(note)}</small>` : ''}</td></tr>`;

/** 妖力のフラグ（surge:fast・surge:boost・surge:on）を読む */
function surgeFlags(lo: BattleLoadout) {
  let delta = 0;
  const boost: Record<number, number> = {};
  const on: Record<number, string[]> = {};
  for (const f of lo.flags) {
    const p = f.split(':');
    if (p[0] !== 'surge') continue;
    if (p[1] === 'fast') delta += Number(p[2] ?? -1) || 0;
    else if (p[1] === 'boost' && p[2] === lo.charId && p[3]) {
      const [st, v] = p[3].split('=').map(Number);
      if (st && v) boost[st] = v;
    } else if (/^on\d$/.test(p[1] ?? '')) (on[Number(p[1].slice(2))] ??= []).push(p[2] ?? '');
  }
  return { delta, boost, on };
}

/** 術の中身（倍率・射程・待ち・範囲など。スキル・装備の上乗せ込み）と奥義 */
export function artInfoDialog(charId: CharId, lo: BattleLoadout | null) {
  const sk = CHARS[charId].skill;
  const power = lo?.skill.power ?? 1;
  const cd = Math.max(1, sk.cd + (lo?.skill.cdDelta ?? 0));
  const splash = (sk.splash ?? 0) + (lo?.skill.splashBonus ?? 0);
  const rows = [
    row('威力', `攻撃の${times(sk.mult * power)}`, power !== 1 ? `基本${times(sk.mult)}×術の威力${power > 1 ? '+' : ''}${pct(power - 1)}` : ''),
    row('射程', `${sk.range}マス`, sk.line ? '直線の上だけ' : ''),
    row('待ち', `使った後 ${cd}手番`, cd !== sk.cd ? `基本${sk.cd}手番` : ''),
    sk.magic ? row('魔法', '防御を半分無視') : '',
    splash > 0 ? row('範囲', `周りに${pct(splash)}`) : '',
    sk.pierce ? row('貫通', `奥の${sk.pierce}体まで`) : '',
    sk.delay ? row('遅らせる', '相手の次の行動を遅らせる') : '',
    sk.leech ? row('吸精', `与ダメの${pct(sk.leech)}を回復`) : '',
    sk.coreMult && sk.coreMult !== 1 ? row('本陣へ', times(sk.coreMult)) : '',
    sk.buildingMult && sk.buildingMult !== 1 ? row('建物へ', times(sk.buildingMult)) : '',
  ].join('');
  const og = CFG.surge.ougi.chars[charId];
  const hasOugi = !!lo?.flags.includes('surge:ougi');
  const ougi = og
    ? `<div class="ca-ougi"><b>${icon('star')}奥義「${esc(og.name)}」<em>${hasOugi ? '装着中' : '未装着'}</em></b><p>${esc(og.desc)}。妖力が参のとき術の代わりに放てる（放つと妖力は壱に戻る）。${hasOugi ? '' : `${ougiSummary(charId, lo)?.line ?? ''}。`}</p></div>`
    : '';
  openModal(
    `<div class="sk-info ca-info"><header class="ed-head"><span class="ed-icon got">${icon('skill')}</span><div class="ed-name"><h2>${esc(sk.name)}</h2><small class="ed-br">${esc(CHARS[charId].name)}の術</small></div></header>
      <table class="ca-table" aria-label="${esc(sk.desc)}">${rows}</table>
      ${ougi}</div>`,
    { title: '術' },
  );
}

/** 妖力の上がり方（必要な撃破ポイント・段階の効果・キャラの特性）と、今の構成での上乗せ */
export function surgeInfoDialog(charId: CharId, lo: BattleLoadout | null) {
  const S = CFG.surge;
  const f = lo ? surgeFlags(lo) : { delta: 0, boost: {} as Record<number, number>, on: {} as Record<number, string[]> };
  const th: number[] = [];
  S.thresholds.forEach((t, i) => th.push(i === 0 ? t : Math.max(th[i - 1] + 1, t + f.delta)));
  const ON: Record<string, string> = { skill: '上がった瞬間、術がすぐ使える', heal: '上がった瞬間、HPが全回復', mat: '上がった瞬間、陣材+5' };
  const stages = S.stageNames
    .map((name, i) => {
      const st = i + 1;
      const e = S.stages[i];
      const common = st === 1 ? '基本の強さ' : `攻撃・HP +${pct(e.atk)}${e.heal ? `・上がった瞬間HP${pct(e.heal)}回復` : ''}`;
      // 上の行は全キャラ共通の段階効果、箇条書きはこのキャラだけの特性
      const traits = (S.traits[charId]?.[st] ?? []).map((t) => `<li><small class="ca-own">${esc(CHARS[charId].name)}</small><b>${esc(t.name)}</b>${esc(t.desc)}${f.boost[st] ? `<em>効果${times(f.boost[st])}</em>` : ''}</li>`).join('');
      const on = (f.on[st] ?? []).map((k) => `<li>${esc(ON[k] ?? k)}</li>`).join('');
      return `<section class="ca-stage"><header><b class="ca-st">${esc(name)}</b><small>${st === 1 ? '始まり' : `撃破ポイント ${th[i]}点`}</small></header>
        <p>${esc(common)}</p>${traits || on ? `<ul>${traits}${on}</ul>` : ''}</section>`;
    })
    .join('');
  const P = S.points;
  // 貯まり方の説明は (i) を押したときだけ開く
  const m = openModal(
    `<div class="sk-info ca-info"><header class="ed-head"><span class="ed-icon got">${icon('star')}</span><div class="ed-name"><h2>${esc(S.label)}</h2><small class="ed-br">${esc(CHARS[charId].name)}・戦闘中だけ育つ力</small></div><button type="button" class="icon-btn ca-help" data-help aria-label="撃破ポイントの貯まり方" aria-expanded="false">${icon('info')}</button></header>
      <div class="ca-help-body" hidden><p class="ed-desc">相手を倒すと撃破ポイントが貯まり、段階が上がる：中立の妖 ${P.neutral}点・相手ヒーロー ${P.hero}点・相手の建物 ${P.building}点。番人・章ボスを倒すと即1段上がる。${S.koReset === 'all' ? '倒されると壱に戻る。' : S.koReset === 'one' ? '倒されると1段下がる。' : ''}</p></div>
      ${f.delta ? `<p class="ed-note">${icon('star')}スキルで必要な撃破ポイント${f.delta}</p>` : ''}
      <div class="ca-stages">${stages}</div></div>`,
    { title: '妖力' },
  );
  m.body.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-help]');
    if (!b) return;
    const body = m.body.querySelector<HTMLElement>('.ca-help-body')!;
    body.hidden = !body.hidden;
    b.setAttribute('aria-expanded', String(!body.hidden));
    b.classList.toggle('on', !body.hidden);
  });
}

/** 能力値タブの札の一行：術（倍率・射程・待ち） */
export function artSummary(charId: CharId, lo: BattleLoadout | null) {
  // 持っていく術が基本の術でなければ、その術の名前と効果
  const ju = lo?.jutsu ? JUTSU_BY_ID[lo.jutsu] : undefined;
  if (ju && ju.charId === charId) return { name: ju.name, line: ju.desc };
  const sk = CHARS[charId].skill;
  const cd = Math.max(1, sk.cd + (lo?.skill.cdDelta ?? 0));
  return { name: sk.name, line: `攻撃の${times(sk.mult * (lo?.skill.power ?? 1))}・射程${sk.range}・待ち${cd}手番` };
}

/** 能力値タブの札の一行：妖力（弐・参の特性の名前） */
export function surgeSummary(charId: CharId, _lo: BattleLoadout | null) {
  const T = CFG.surge.traits[charId] ?? {};
  // 弐を名前の行、参を下の行に（術・奥義の札と同じ2行の高さ）
  const [two, three] = [2, 3].map((st) => `${CFG.surge.stageNames[st - 1]} ${(T[st] ?? []).map((t) => t.name).join('・')}`);
  return { name: two, line: three };
}

/** 能力値タブの札の一行：奥義（装着していなければ解放の案内） */
export function ougiSummary(charId: CharId, lo: BattleLoadout | null) {
  const og = CFG.surge.ougi.chars[charId];
  if (!og) return null;
  const on = !!lo?.flags.includes('surge:ougi');
  const ju = lo?.jutsu ? JUTSU_BY_ID[lo.jutsu] : undefined;
  // 奥義はその術の系統を Lv7 まで上げると覚える（スキルの系統。主Lv は関係ない）
  const own = ju && ju.charId === charId ? ju : JUTSU.find((x) => x.charId === charId && x.kind === 'strike');
  const line = own ? skillLines(charId)?.find((l) => l.col === own.col) : undefined;
  const how = line ? `スキル「${line.name}」Lv${LINE_OUGI_LV}で解放` : `スキルの系統 Lv${LINE_OUGI_LV}で解放`;
  if (ju && ju.charId === charId) return { name: ju.ougiName, on, line: on ? '妖力 参で術の代わりに' : how };
  return { name: og.name, on, line: on ? '妖力 参で術の代わりに' : how };
}
